import React, { useState, useRef, useEffect } from 'react';
import { MoreVertical, type LucideIcon } from 'lucide-react';
import './ContextMenu.css';

export interface ContextMenuItem {
  /** Unique identifier for the menu item */
  id: string;
  /** Display label */
  label: string;
  /** Optional Lucide icon component */
  icon?: LucideIcon;
  /** Whether the item is disabled */
  disabled?: boolean;
  /** Whether this is a danger/destructive action (shows red) */
  danger?: boolean;
  /** Click handler */
  onClick: () => void;
}

export interface ContextMenuProps {
  /** Menu items to display */
  items: ContextMenuItem[];
  /** Custom trigger element (defaults to MoreVertical icon button) */
  trigger?: React.ReactNode;
  /** Optional class name */
  className?: string;
  /** Position of menu relative to trigger */
  position?: 'bottom-right' | 'bottom-left';
  /** Aria label for the trigger button */
  ariaLabel?: string;
}

/**
 * Context menu with dropdown triggered by three-dots button.
 * Supports icons, disabled states, and danger styling.
 */
export const ContextMenu: React.FC<ContextMenuProps> = ({
  items,
  trigger,
  className = '',
  position = 'bottom-right',
  ariaLabel = 'Open menu',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleItemClick = (item: ContextMenuItem) => {
    if (!item.disabled) {
      item.onClick();
      setIsOpen(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent, item: ContextMenuItem) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleItemClick(item);
    }
  };

  return (
    <div className={`context-menu ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        className="context-menu-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        {trigger || <MoreVertical className="context-menu-trigger-icon" />}
      </button>

      {isOpen && (
        <div
          ref={menuRef}
          className={`context-menu-dropdown context-menu-dropdown--${position}`}
          role="menu"
        >
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                className={`context-menu-item ${
                  item.disabled ? 'context-menu-item--disabled' : ''
                } ${item.danger ? 'context-menu-item--danger' : ''}`}
                onClick={() => handleItemClick(item)}
                onKeyDown={(e) => handleKeyDown(e, item)}
                disabled={item.disabled}
                role="menuitem"
                tabIndex={item.disabled ? -1 : 0}
              >
                {Icon && <Icon className="context-menu-item-icon" />}
                <span className="context-menu-item-label">{item.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ContextMenu;
